/**
 * FilesTab — every file attached to the project (direct uploads + files
 * embedded from the description document).
 */
import React, { useEffect, useRef, useState, useCallback } from "react";
import { toast } from "../../../utils/toast";
import { Upload, FileText, Image as ImageIcon, Trash2, ExternalLink } from "lucide-react";
import { wsApi, assetUrl } from "../wsApi";
import { T, ConfirmDialog } from "../../../components/em/ui";
import { Skeleton } from "../wsUi";

const isImg = (n = "") => /\.(png|jpe?g|gif|webp|svg)$/i.test(n);

// Must match middleware/workspaceUpload.js — checked here too so an oversized
// or wrong-typed file is rejected instantly instead of after a full upload.
const MAX_FILE_BYTES = 10 * 1024 * 1024;
const MAX_FILE_LABEL = "10 MB";
const ALLOWED_RE = /\.(jpe?g|png|gif|webp|pdf|docx?|xlsx?|pptx?|txt|csv|zip)$/i;

// index.css skins every bare <button> with height:40px / line-height:40px, so
// the row's icon buttons state their own box — otherwise the trash glyph sits
// at the top of a 40px block, out of line with the open-link icon beside it.
const rowIconBtn = {
  width: 30, height: 30, display: "inline-flex", alignItems: "center", justifyContent: "center",
  flexShrink: 0, padding: 0, margin: 0, lineHeight: 1, border: 0, borderRadius: 8,
  background: "none", cursor: "pointer", textDecoration: "none",
};

const fmtSize = (b) =>
  b < 1024 * 1024
    ? `${Math.max(1, Math.round(b / 1024))} KB`
    : `${(b / 1024 / 1024).toFixed(1)} MB`;

// The API returns two error shapes: controllers send { error: "text" }, the
// central handler sends { error: { code, message } }. Narrow to a string so
// the real reason reaches the toast instead of a generic "Upload failed".
const serverMessage = (err, fallback) => {
  const e = err?.response?.data?.error;
  if (typeof e === "string") return e;
  if (e && typeof e === "object") return e.message || fallback;
  return fallback;
};

export default function FilesTab({ projectId, canManage }) {
  const [files, setFiles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  // A file over the proxy's body limit goes up in slices (see wsApi), so a big
  // upload is many requests and takes long enough to need a progress readout.
  const [pct, setPct] = useState(0);
  const ref = useRef(null);

  const load = useCallback(() => {
    setLoading(true);
    wsApi.listProjectFiles(projectId).then(setFiles)
      .catch(() => toast.error("Could not load files"))
      .finally(() => setLoading(false));
  }, [projectId]);

  useEffect(() => { load(); }, [load]);

  const upload = async (file) => {
    if (!file) return;
    // Caught here so the user is told immediately, and a 10 MB+ file isn't
    // pushed over the wire just to be refused.
    if (file.size > MAX_FILE_BYTES) {
      toast.error(
        `"${file.name}" is ${fmtSize(file.size)} — the limit is ${MAX_FILE_LABEL}. Please upload a smaller file.`
      );
      return;
    }
    if (!ALLOWED_RE.test(file.name)) {
      toast.error(`"${file.name}" isn't a supported file type.`);
      return;
    }
    setBusy(true);
    setPct(0);
    try {
      await wsApi.uploadProjectFile(projectId, file, "upload", (p) => setPct(p));
      load();
    }
    catch (err) {
      // The size check above already passed and anything sizeable now goes up
      // in sub-MB slices, so a 413 means the proxy's body limit is lower still
      // — lower than a single chunk. Say that, rather than blaming a 10 MB cap
      // the file is nowhere near.
      toast.error(
        serverMessage(
          err,
          err?.response?.status === 413
            ? `The server refused "${file.name}" even in slices — its request size limit ` +
              "is set lower than this app can work with. Ask an admin to raise it."
            : "Upload failed. Please try again."
        )
      );
    }
    finally { setBusy(false); setPct(0); }
  };

  const [confirming, setConfirming] = useState(null);
  const [deleting, setDeleting] = useState(false);

  const del = async () => {
    if (!confirming) return;
    setDeleting(true);
    try {
      await wsApi.deleteProjectFile(projectId, confirming.id);
      setFiles((f) => f.filter((x) => x.id !== confirming.id));
      toast.success(`"${confirming.original_name || "File"}" deleted`);
      setConfirming(null);
    } catch (err) { toast.error(serverMessage(err, "Could not delete the file. Please try again.")); }
    finally { setDeleting(false); }
  };

  return (
    <div>
      <div style={{ display: "flex", justifyContent: "flex-end", marginBottom: 14 }}>
        <button onClick={() => ref.current?.click()} disabled={busy}
          style={{ display: "inline-flex", alignItems: "center", gap: 7, padding: "8px 14px", borderRadius: 8, border: 0, cursor: "pointer", background: T.gradient, color: "#fff", font: `600 12.5px ${T.font}`, boxShadow: T.shadowBrand }}>
          <Upload size={14} />
          {busy ? `Uploading… ${Math.round(pct * 100)}%` : "Upload file"}
        </button>
        <input ref={ref} type="file" style={{ display: "none" }}
          onChange={(e) => { upload(e.target.files?.[0]); e.target.value = ""; }} />
      </div>

      {/* State the limit up front rather than only on failure. */}
      <div style={{ font: `400 11.5px ${T.font}`, color: T.fg7, textAlign: "right", marginTop: -6, marginBottom: 12 }}>
        Max {MAX_FILE_LABEL} per file
      </div>

      {loading ? (
        <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
          {Array.from({ length: 4 }, (_, i) => <Skeleton key={i} h={54} radius={12} />)}
        </div>
      ) : files.length === 0 ? (
        <div style={{ textAlign: "center", padding: 46, color: T.fg7, background: "var(--em-bg-card)", border: `1px dashed ${T.border}`, borderRadius: 12 }}>
          <FileText size={36} style={{ opacity: 0.5, marginBottom: 10 }} />
          <div style={{ font: `600 14px ${T.font}`, color: T.fg1 }}>No files yet</div>
          <div style={{ font: `400 12px ${T.font}`, marginTop: 4 }}>Upload files or embed them in the description.</div>
        </div>
      ) : (
        <div style={{ background: "var(--em-bg-card)", border: `1px solid ${T.border}`, borderRadius: 12, overflow: "hidden" }}>
          {files.map((f, i) => (
            <div key={f.id} style={{ display: "flex", alignItems: "center", gap: 12, padding: "12px 16px", borderTop: i ? `1px solid ${T.divider}` : "none" }}>
              <div style={{ width: 32, height: 32, borderRadius: 8, background: T.bgHead, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0, color: T.fg5 }}>
                {isImg(f.original_name) ? <ImageIcon size={15} /> : <FileText size={15} />}
              </div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ font: `500 13px ${T.font}`, color: T.fg1, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                  {f.original_name || "File"}
                </div>
                <div style={{ font: `400 11px ${T.font}`, color: T.fg7, marginTop: 1 }}>
                  {f.source === "description" ? "From description" : "Uploaded"}
                  {f.uploaded_by_name ? ` · ${f.uploaded_by_name}` : ""}
                </div>
              </div>
              <div style={{ display: "flex", alignItems: "center", gap: 4, flexShrink: 0 }}>
                <a href={assetUrl(f.file_url)} target="_blank" rel="noreferrer" title="Open"
                  aria-label={`Open ${f.original_name || "file"}`}
                  style={{ ...rowIconBtn, color: T.fg5 }}>
                  <ExternalLink size={15} />
                </a>
                {canManage && (
                  <button type="button" onClick={() => setConfirming(f)} title="Delete"
                    aria-label={`Delete ${f.original_name || "file"}`}
                    style={{ ...rowIconBtn, color: T.fg7 }}>
                    <Trash2 size={15} />
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      <ConfirmDialog
        open={!!confirming}
        title="Delete file?"
        message={`Are you sure you want to delete "${confirming?.original_name || "this file"}"? It will be removed from the project and can't be recovered.`}
        confirmLabel="Delete file"
        danger
        busy={deleting}
        onConfirm={del}
        onCancel={() => !deleting && setConfirming(null)}
      />
    </div>
  );
}
